import { Json } from "@redotech/json/json";
import { ReturnAddress } from "@redotech/redo-model/return";
import { Address } from "@redotech/redo-model/return-flow";
import axios, { AxiosHeaders } from "axios";
import { REDO_API_URL } from "./config";

const TOKEN_KEY = "redo.portal_auth_token";

const client = () => {
  const widgetId = getWidgetId();
  if (!widgetId) {
    throw new Error("Missing ?widget_id");
  }
  const base = `${REDO_API_URL}/widgets/${widgetId}`;
  const headers = new AxiosHeaders();
  const client = axios.create({ baseURL: base, headers });
  return client;
};

export const getWidgetId = () => {
  let widgetId = new URLSearchParams(window.location.search).get("widget_id");
  if (!widgetId && widget_id !== "replace_me") {
    widgetId = widget_id;
  }
  return widgetId;
};

const authentication = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return {};
  }
  return { Authorization: `Bearer ${token}` };
};

export const getOrders = (params) => {
  return client().get("orders", { headers: authentication(), params });
};

export const getOrderById = (id) => {
  return client().get(`orders/${id}`, { headers: authentication() });
};

export const getReturn = (id) => {
  return client().get(`returns/${id}`, { headers: authentication() });
};

export const getCurrencyExchangeRates = () => {
  return client().get("currency-conversion-rates", {
    headers: authentication(),
  });
};

export type CreateAuthRequest =
  | {
      type: "order";
      email: string;
      orderNumber: string;
      isGift: boolean;
      emailOrZip: string;
    }
  | { type: "email"; token: string };

export interface CreateAuthResponse {
  token: string;
  customer: { email: string; name: string };
  orderID: string;
}

/**
 * POST /widgets/:id/customer-auth
 */
export const createAuth = async (
  request: CreateAuthRequest,
): Promise<CreateAuthResponse> => {
  let body: Json;
  switch (request.type) {
    case "email":
      body = { token: request.token };
      break;
    case "order":
      body = {
        email: request.email,
        orderNumber: request.orderNumber,
        isGift: request.isGift,
        emailOrZip: request.emailOrZip,
      };
      break;
  }
  const response = await client().post("customer-auth", body, {
    params: { type: request.type },
  });
  return response.data;
};

export interface SendAuthEmailRequest {
  email: string;
  url: string;
}

export interface SendAuthEmailResponse {}

/**
 * POST /widgets/:id/customer-auth/email
 */
export const sendAuthEmail = async (
  request: SendAuthEmailRequest,
): Promise<SendAuthEmailResponse> => {
  const response = await client().post("customer-auth/email", request);
  return response.data;
};

export const getSettings = () => {
  return client().get("");
};

export const getProduct = (id) => {
  return client().get(`products/${id}`, { headers: authentication() });
};

export const getProductTaxes = (params: {
  products: {
    variantId: number;
    price: string;
  }[];
  shippingAddress: ReturnAddress;
  orderId: string;
}) => {
  return client().post("products/taxes", { headers: authentication(), params });
};

export const getShopifyOrder = (shopifyOrderId: number) => {
  return client().get(`shopify/order/${shopifyOrderId}`, {
    headers: authentication(),
  });
};

export const getProducts = () => {
  return client().get("shopify/products", { headers: authentication() });
};

export const getProductsByTags = (tags: string) => {
  return client().get(`shopify/products?tags=${tags}`, {
    headers: authentication(),
  });
};

export const getCollectionProducts = (collectionId: number) => {
  return client().get(`shopify/collections/${collectionId}/products`, {
    headers: authentication(),
  });
};

export const getLocations = () => {
  return client().get("shopify/locations", { headers: authentication() });
};

export const getRecommendations = (productId: number) => {
  return client().get(`shopify/recommendations/${productId}`, {
    headers: authentication(),
  });
};

export const searchProducts = (searchQuery: string) => {
  return client().get(`shopify/search/${searchQuery}`, {
    headers: authentication(),
  });
};

export const getShippingFee = (params) => {
  return client().put("returns/fee", { headers: authentication(), params });
};

export const getAutomations = () => {
  return client().get("automations");
};

export const uploadImages = (params) => {
  return client().post("upload", params, {
    headers: {
      ...authentication(),
      "Content-Type": "multipart/form-data",
    },
  });
};

export const submitReturn = (params) => {
  return client().post("returns/submit", { headers: authentication(), params });
};

export const createCart = (params) => {
  return client().post("cart/create", { headers: authentication(), params });
};

export const getCart = (params) => {
  return client().post("cart/get", { headers: authentication(), params });
};

export const addItemToCart = (params) => {
  return client().post("cart/add", { headers: authentication(), params });
};

export const removeItemFromCart = (params) => {
  return client().post("cart/remove", { headers: authentication(), params });
};

export const addDiscountCodesToCart = (params) => {
  return client().post("cart/discounts", { headers: authentication(), params });
};

export const recreateExpiredDiscountCodes = (params: {
  codes: string[];
  storeUrl: string;
}) => {
  return client().post("recreate-discount-codes", {
    headers: authentication(),
    params,
  });
};

export const getCollectionsForProduct = (productId: string) => {
  return client().get(`/shopify/products/${productId}/collections`, {
    headers: authentication(),
  });
};

export const completeExchange = (token: string) => {
  return client().get(`/returns/completeExchange?token=${token}`);
};

export const verifyAddress = async (address: Address) => {
  const response = await client().post("returns/address/verify", address, {
    headers: authentication(),
  });
  return response.data;
};

export const getProductsBySkus = (skus: string[]) => {
  return client().get(`/products?skus=${skus.join(",")}`);
};

export const createAstraUserIntent = (params) => {
  return client().post("/astra-user-intent", {
    headers: authentication(),
    params,
  });
};
